import { motion } from "framer-motion";
import {
  BookOpen,
  MessageSquare,
  Users,
  Image,
  Send,
  Zap,
  Code,
  Rocket,
  Shield,
  CheckCircle,
} from "lucide-react";

const DefaultContent = ({ setActiveSection }) => {
  const quickLinks = [
    {
      icon: Rocket,
      title: "Quick Start",
      description: "Get up and running in minutes",
      section: "quick-start",
      color: "from-[#01CC54]/10 to-[#048AAD]/10",
      borderColor: "border-[#01CC54]/30",
      iconColor: "text-[#01CC54]",
    },
    {
      icon: Send,
      title: "Send Messages",
      description: "Learn how to send single and bulk messages",
      section: "messages",
      color: "from-blue-50 to-blue-100/50",
      borderColor: "border-blue-200",
      iconColor: "text-blue-600",
    },
    {
      icon: Users,
      title: "Group Messages",
      description: "Send messages to WhatsApp groups",
      section: "group-messages",
      color: "from-violet-50 to-violet-100/50",
      borderColor: "border-violet-200",
      iconColor: "text-violet-600",
    },
    {
      icon: Image,
      title: "Media & Documents",
      description: "Upload and send images, videos, and files",
      section: "image-document",
      color: "from-amber-50 to-amber-100/50",
      borderColor: "border-amber-200",
      iconColor: "text-amber-600",
    },
    {
      icon: Users,
      title: "Groups",
      description: "Manage and retrieve group information",
      section: "groups",
      color: "from-purple-50 to-purple-100/50",
      borderColor: "border-purple-200",
      iconColor: "text-purple-600",
    },
    {
      icon: MessageSquare,
      title: "Chats",
      description: "Handle incoming messages and conversations",
      section: "chat",
      color: "from-emerald-50 to-emerald-100/50",
      borderColor: "border-emerald-200",
      iconColor: "text-emerald-600",
    },
    {
      icon: Code,
      title: "API Reference",
      description: "Complete API documentation and examples",
      section: "api",
      color: "from-indigo-50 to-indigo-100/50",
      borderColor: "border-indigo-200",
      iconColor: "text-indigo-600",
    },
  ];

  return (
    <div className="space-y-6 px-3 py-4 sm:px-4 lg:px-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#01CC54]/10 to-[#048AAD]/10 border border-[#01CC54]/20 mb-4">
          <BookOpen className="w-4 h-4 text-[#01CC54]" />
          <span className="text-sm font-semibold text-[#01CC54]">Documentation</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 text-gray-900 leading-tight">
          Welcome to <span className="text-[#01CC54]">Ping</span> API Docs
        </h1>
        <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Everything you need to integrate WhatsApp Business API into your application.
          Get started with our comprehensive guides and API reference.
        </p>
      </motion.div>

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickLinks.map((link, index) => {
          const Icon = link.icon;
          return (
            <motion.div
              key={link.section}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative"
            >
              <button
                onClick={() => setActiveSection(link.section)}
                className={`w-full h-full text-left p-4 rounded-xl border ${link.borderColor} bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${link.color} ${link.borderColor} border flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                  <Icon className={`w-5 h-5 ${link.iconColor}`} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{link.title}</h3>
                <p className="text-xs text-gray-600 mb-3">{link.description}</p>
                <span className={`text-xs font-semibold ${link.iconColor} group-hover:underline`}>
                  Explore →
                </span>
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Features Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="rounded-2xl p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-lg"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#01CC54]/20 to-[#01CC54]/10 border border-[#01CC54]/30 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#01CC54]" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Key Features</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#01CC54]/10 border border-[#01CC54]/30 flex items-center justify-center flex-shrink-0 mt-1">
              <CheckCircle className="w-5 h-5 text-[#01CC54]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Easy Integration</h3>
              <p className="text-xs text-gray-600">
                Simple REST API endpoints for seamless integration
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#01CC54]/10 border border-[#01CC54]/30 flex items-center justify-center flex-shrink-0 mt-1">
              <Shield className="w-5 h-5 text-[#01CC54]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Secure & Reliable</h3>
              <p className="text-xs text-gray-600">
                Enterprise-grade security with reliable message delivery
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#01CC54]/10 border border-[#01CC54]/30 flex items-center justify-center flex-shrink-0 mt-1">
              <Rocket className="w-5 h-5 text-[#01CC54]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Fast & Scalable</h3>
              <p className="text-xs text-gray-600">
                Send thousands of messages with high performance
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="rounded-2xl p-6 bg-gradient-to-br from-[#01CC54]/10 via-[#048AAD]/10 to-[#01CC54]/10 border border-[#01CC54]/30 text-center"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Ready to Get Started?
        </h2>
        <p className="text-sm text-gray-600 mb-5 max-w-xl mx-auto">
          Start with our Quick Start guide to set up your first WhatsApp instance
          and send your first message in minutes.
        </p>
        <button
          onClick={() => setActiveSection("quick-start")}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#048AAD] hover:bg-[#048AAD]/90 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        >
          <Rocket className="w-4 h-4" />
          Get Started Now
        </button>
      </motion.div>
    </div>
  );
};

export default DefaultContent;
